import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Building2, Globe, Mail, KeyRound } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedClientForReset, setSelectedClientForReset] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
    website: "",
    active: true
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API_URL}/clients/${editingClient.id}`, formData);
        toast.success("Cliente atualizado com sucesso");
      } else {
        await axios.post(`${API_URL}/clients`, formData);
        toast.success("Cliente e conta de acesso criados com sucesso");
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar cliente");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      company: client.company,
      email: client.email,
      password: "",  // Don't show existing password
      website: client.website || "",
      active: client.active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Tem certeza que deseja eliminar este cliente? Isto também eliminará a conta de acesso.")) return;
    try {
      await axios.delete(`${API_URL}/clients/${clientId}`);
      toast.success("Cliente eliminado");
      fetchClients();
    } catch (error) {
      toast.error("Erro ao eliminar cliente");
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ name: "", company: "", email: "", password: "", website: "", active: true });
  };

  const handleResetPassword = async () => {
    if (!selectedClientForReset || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error("A password deve ter pelo menos 6 caracteres");
      return;
    }
    try {
      await axios.put(`${API_URL}/clients/${selectedClientForReset.id}/reset-password`, {
        password: newPassword
      });
      toast.success(`Password de ${selectedClientForReset.name} alterada com sucesso`);
      setResetDialogOpen(false);
      setSelectedClientForReset(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar password");
    }
  };

  const openResetDialog = (client) => {
    setSelectedClientForReset(client);
    setNewPassword("");
    setResetDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <Card><CardContent className="pt-6"><div className="skeleton h-64" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <Users className="w-8 h-8 text-blue-600" />
            Gestão de Clientes
          </h1>
          <p className="text-slate-500 mt-1">
            Gerir clientes da agência
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                {editingClient ? "Editar Cliente" : "Novo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Contacto</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="João Silva"
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Empresa</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Empresa Lda"
                  required
                  data-testid="client-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (usado para login)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@empresa.pt"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  Password {editingClient && <span className="text-slate-400 font-normal">(deixe vazio para manter)</span>}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required={!editingClient}
                  data-testid="client-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website (opcional)</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://empresa.pt"
                  data-testid="client-website-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="active" className="font-normal">Cliente ativo</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-client-btn">
                  {editingClient ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <KeyRound className="w-5 h-5 text-amber-600" />
              Repor Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Cliente:</p>
              <p className="font-medium text-slate-900">{selectedClientForReset?.name}</p>
              <p className="text-sm text-slate-500">{selectedClientForReset?.email}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nova Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleResetPassword} 
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={!newPassword || newPassword.length < 6}
                data-testid="confirm-reset-password-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Alterar Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-100">
          <div className="text-blue-600 text-sm font-medium">Total Clientes</div>
          <div className="text-2xl font-bold text-blue-900 mt-1" style={{ fontFamily: 'Manrope' }}>
            {clients.length}
          </div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-100">
          <div className="text-emerald-600 text-sm font-medium">Clientes Ativos</div>
          <div className="text-2xl font-bold text-emerald-900 mt-1" style={{ fontFamily: 'Manrope' }}>
            {clients.filter(c => c.active).length}
          </div>
        </Card>
      </div>

      {/* Clients Table */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
            Lista de Clientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Website</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          {client.company}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {client.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {client.website ? (
                          <a href={client.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline">
                            <Globe className="w-4 h-4" />
                            {client.website.replace(/https?:\/\//, '').slice(0, 20)}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={client.active ? "badge-success" : "badge-error"}>
                          {client.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openResetDialog(client)}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Repor Password"
                            data-testid={`reset-password-${client.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(client)}
                            title="Editar"
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Ainda não existem clientes registados</p>
              <p className="text-sm mt-1">Clique em "Novo Cliente" para adicionar o primeiro</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminClientsPage;
